import type { Locale } from './localeCodes';
import { VILLAGE_NAMES } from './villageNames';
import { enemyFamilyLabel, npcLabel, roadPoiSettlementLabel, townDisplayById, townLabel } from './contentLabels';

export type DisplayKind = 'hero' | 'npc' | 'town' | 'poi' | 'item' | 'enemy' | 'quest';
export type DisplayEntity = { name?: string | null; nameKey?: string | null; id?: number | null; type?: string | null; title?: string | null; displayLabel?: string | null; eventId?: string | null };

/** One display boundary for API names. The original human name remains the fallback. */
export function displayName(locale: Locale, kind: DisplayKind, entity: DisplayEntity): string {
  const raw = String(entity.name ?? entity.title ?? entity.displayLabel ?? '').trim();
  const key = String(entity.nameKey ?? '').trim();
  const generic: Record<DisplayKind, [string, string]> = {
    hero: ['Герой', 'Hero'], npc: ['Персонаж', 'Character'], town: ['Город', 'Town'], poi: ['Локация', 'Location'],
    item: ['Предмет', 'Item'], enemy: ['Монстр', 'Monster'], quest: ['Задание', 'Quest'],
  };
  const fallback = generic[kind][locale === 'ru' ? 0 : 1];
  const corruptedInnkeeper = /^in+n?keeper of cap(?:t?i)?[at]l(?:\s+\d+)?$/i.test(raw);
  const technical = !raw || /^[a-z][a-z0-9_]*(?:\.[a-z0-9_]+)+$/i.test(raw) || /^[a-z_]+_[a-z0-9_]+(?:_[a-z0-9_]+)*$/i.test(raw) || /^\d+$/.test(raw);
  const human = corruptedInnkeeper ? (locale === 'ru' ? 'Хозяин гостиницы столицы' : 'Capital Innkeeper') : technical ? fallback : raw;
  if (kind === 'town') return key ? townLabel(locale, key, human) : entity.id == null ? human : townDisplayById(locale, entity.id, human);
  if (kind === 'poi') {
    const village = VILLAGE_NAMES[key];
    if (village) return village[locale === 'ru' ? 'ru' : 'en'];
    return technical ? roadPoiSettlementLabel(locale, entity.eventId ?? undefined, human) : human;
  }
  if (kind === 'npc') return npcLabel(locale, key, human);
  if (kind === 'enemy') {
    const localized = enemyFamilyLabel(locale, String(entity.type ?? ''), human);
    return localized && !/^[a-z_]+_[a-z0-9_]+(?:_[a-z0-9_]+)*$/i.test(localized) ? localized : human;
  }
  return human;
}
